import { Grid, Box, Chip, IconButton, Typography } from "@mui/material";
import { Move } from "../../move/move";
import { PokemonV2, rankCorrectionEnum } from "../../pokemon/pokemon";
import type { PokemonOption } from "../../types/pokemon";
import type { AttackPokemonData } from "../../types/attack";
import type { AttackStatInputs } from "../../types/stat";
import type { ItemOption } from "../../types/item";
import { retCalc, PersonalityRate } from "../../types/damage";
import { POKEMON_ITEM_ID } from "../../types/item";
import DisplayDamageCalc from "./DisplayDamageCalc";
import AttackPokemonAbilityInput from "../../components/AttackPokemonAbilityInput";
import OffencePokemonEffortInput from "./OffencePokemonEffortInput";
import AttackPokemonItemInput from "../../components/AttackPokemonItemInput";
import OffencePokemonOtherSettingInput from "./OffencePokemonOtherSettingInput";
import OffencePokemonRankInput from "./OffencePokemonRankInput";
import OffencePokemonSelect from "./OffencePokemonSelect";
import AttackPokemonTeraSelector from "../../components/AttackPokemonTeraSelector";
import SelectMove from "./SelectMove";
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AttackPokemonCardFrame from "../../components/AttackPokemonCardFrame";
import { getArceusEffectiveType } from "../../pokemon/PlateUtils";
import { useState } from "react";
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

function DefenceMode({
  selectedPokemon,
  pokemonOptions,
  attackPokemonList,
  setAttackPokemonList,
  selectedAttackPokemonForMove,
  setSelectedAttackPokemonForMove,
  handleAddAttackPokemon,
  getAllAttackMoves,
  handleSelectMoveForAttackPokemon,
  getMoveDisplayName,
  getMoveDisplayPower,
  calculateDamage,
  getDamageDetail,
  getRankTypeForMove,
  handleUpdateRankCorrection,
  handleSelectTerasuTypeForAttackPokemon,
  handleSelectTerasuStateForAttackPokemon,
  handleSelectItemForAttackPokemon,
  getOffenseItems,
  handleSelectAbilityForAttackPokemon,
  handleUpdateKodaiQuarkStat,
  handleUpdateAttackPokemonStats,
  handleRemoveAttackPokemon,
  handleCopyAttackPokemon,
  handleUpdateCriticalHit,
  handleUpdateAttackPokemonStatus,
  handleUpdateCustomPower,
  handleUpdateJuuden,
  handleUpdateDoublePower,
  handleUpdateDefensePokemonEffort
}: {
  selectedPokemon: PokemonV2 | null;
  pokemonOptions: PokemonOption[];
  attackPokemonList: AttackPokemonData[];
  setAttackPokemonList: (value: React.SetStateAction<AttackPokemonData[]>) => void;
  selectedAttackPokemonForMove: string | null;
  setSelectedAttackPokemonForMove: (a: string | null) => void;
  handleAddAttackPokemon: (event: any, newValue: any) => void;
  getAllAttackMoves: (pokemon: PokemonV2) => Move[];
  handleSelectMoveForAttackPokemon: (id: string, moveId: number) => void;
  getMoveDisplayName: (move: Move, pokemonId: number) => string;
  getMoveDisplayPower: (move: Move, pokemonId: number) => number | string;
  calculateDamage: (attackPokemon: AttackPokemonData, defensePokemon: PokemonV2) => string;
  getDamageDetail: (attackPokemon: AttackPokemonData, defensePokemon: PokemonV2) => retCalc;
  getRankTypeForMove: (move: Move) => string;
  handleUpdateRankCorrection: (id: string, rankCorrection: rankCorrectionEnum) => void;
  handleSelectTerasuTypeForAttackPokemon: (id: string, terasuType: number) => void;
  handleSelectTerasuStateForAttackPokemon: (id: string, terasuState: 'none' | 'type' | 'stellar') => void;
  handleSelectItemForAttackPokemon: (id: string, itemId: POKEMON_ITEM_ID) => void;
  getOffenseItems: ItemOption[];
  handleSelectAbilityForAttackPokemon: (id: string, abilityId: number) => void;
  handleUpdateKodaiQuarkStat: (id: string, value: 'attack' | 'special_attack') => void;
  handleUpdateAttackPokemonStats: (id: string, newStatInputs: AttackStatInputs) => void;
  handleRemoveAttackPokemon: (id: string) => void;
  handleCopyAttackPokemon: (id: string) => void;
  handleUpdateCriticalHit: (id: string, isCritical: boolean, criticalHitCount?: number) => void;
  handleUpdateAttackPokemonStatus: (id: string, isBurned: boolean, isPoisoned: boolean, isParalyzed: boolean) => void;
  handleUpdateCustomPower: (id: string, customPower: number) => void;
  handleUpdateJuuden: (id: string, isJuuden: boolean) => void;
  handleUpdateDoublePower: (id: string, isDoublePower: boolean) => void;
  handleUpdateDefensePokemonEffort: (h_effort: number, b_effort: number, d_effort: number, b_personality?: PersonalityRate, d_personality?: PersonalityRate) => void;
}) {
  const [collapsedCardIds, setCollapsedCardIds] = useState<Set<string>>(new Set());

  const toggleCard = (id: string) => {
    setCollapsedCardIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  
  return <>
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <OffencePokemonSelect
        pokemonOptions={pokemonOptions}
        handleAddAttackPokemon={handleAddAttackPokemon}
      />
      <SelectMove
        selectedAttackPokemonForMove={selectedAttackPokemonForMove}
        attackPokemonList={attackPokemonList}
        getAllAttackMoves={getAllAttackMoves}
        handleSelectMoveForAttackPokemon={handleSelectMoveForAttackPokemon}
        setSelectedAttackPokemonForMove={setSelectedAttackPokemonForMove}
        getMoveDisplayName={getMoveDisplayName}
        getMoveDisplayPower={getMoveDisplayPower}
      />
    </Grid>

    {attackPokemonList.length > 0 ? (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        flex: 1,
        overflowY: 'auto',
        minHeight: 0
      }}>
        {attackPokemonList.map((attackPokemon) => {

          // Determine background color based on damage result
          let backgroundColor = 'white';
          if (attackPokemon.selectedMoveId && selectedPokemon && attackPokemon.selectedMove) {
            const damageResult = calculateDamage(attackPokemon, selectedPokemon);
            backgroundColor = '#c8e6c9'; // Default green
            if (damageResult.includes('確定1発')) {
              backgroundColor = '#ffcdd2'; // Red for guaranteed 1-shot
            } else if (damageResult.includes('乱数1発')) {
              backgroundColor = '#ffe0b2'; // Orange for possible 1-shot
            }
          } else if (attackPokemon.selectedMoveId) {
            backgroundColor = '#f8f9fa'; // Gray for completed but no defense Pokemon
          }

          const border = selectedAttackPokemonForMove === attackPokemon.id
            ? '2px solid #1976d2'
            : attackPokemon.selectedMoveId
              ? '2px solid #4caf50'
              : '1px solid #e0e0e0';
          const isSelectable = !attackPokemon.selectedMoveId;
          // プレートやフォルム差分を加味した表示用タイプを取得する
          const pokemonTypeIdList = getArceusEffectiveType({
            id: attackPokemon.pokemon.id,
            species_id: attackPokemon.pokemon.species_id,
            type_id_list: attackPokemon.pokemon.type_id_list,
            selected_item_id: attackPokemon.selectedItemId ?? attackPokemon.pokemon.selected_item_id,
          });
          const isCollapsed = collapsedCardIds.has(attackPokemon.id);
          const moveLabel = attackPokemon.selectedMoveId && attackPokemon.selectedMove
            ? getMoveDisplayName(attackPokemon.selectedMove, attackPokemon.pokemon.id)
            : '技未選択';

          return (
            <AttackPokemonCardFrame
              key={attackPokemon.id}
              pokemonId={attackPokemon.pokemon.id}
              pokemonName={attackPokemon.pokemon.name_ja}
              pictureUrl={attackPokemon.pokemon.picture_url}
              pokemonTypeIdList={pokemonTypeIdList}
              backgroundColor={backgroundColor}
              border={border}
              cursor={isSelectable ? 'pointer' : 'default'}
              hoverBackgroundColor={isSelectable ? '#f5f5f5' : undefined}
              onClick={
                isSelectable
                  ? () => {
                      setSelectedAttackPokemonForMove(attackPokemon.id);
                    }
                  : undefined
              }
              actions={
                <>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCard(attackPokemon.id);
                    }}
                    sx={{ color: 'text.secondary' }}
                  >
                    {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleCopyAttackPokemon(attackPokemon.id)}
                    sx={{
                      color: 'primary.main',
                      '&:hover': {
                        color: 'primary.dark',
                        backgroundColor: 'primary.light',
                      },
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveAttackPokemon(attackPokemon.id)}
                    sx={{
                      color: 'error.main',
                      '&:hover': {
                        color: 'error.dark',
                        backgroundColor: 'error.light',
                      },
                    }}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </>
              }
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5, width: '100%' }}>
          {isCollapsed && (
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              {moveLabel}
            </Typography>
          )}
                  {!attackPokemon.selectedMoveId && selectedAttackPokemonForMove === attackPokemon.id ? (
                    <Chip label="技選択中" size="small" color="primary" sx={{ fontSize: '0.7rem', height: 20 }} />
                  ) : !attackPokemon.selectedMoveId ? (
                    <Chip
                      label="技未選択"
                      size="small"
                      color="default"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  ) : null}
                  <Box sx={{ flex: 1 }} />
                </Box>
              </Box>

              {!isCollapsed && attackPokemon.selectedMoveId && selectedPokemon && attackPokemon.selectedMove && (
                <>
                  <Box sx={{ flex: 1, display: 'flex', gap: 0.05 }}>
                    <DisplayDamageCalc
                      attackPokemon={attackPokemon}
                      selectedPokemon={selectedPokemon}
                      calculateDamage={calculateDamage}
                      getDamageDetail={getDamageDetail}
                      getMoveDisplayName={getMoveDisplayName}
                      getMoveDisplayPower={getMoveDisplayPower}
                    />
                  </Box>

                  <AttackPokemonTeraSelector
                    variant="stateful"
                    attackPokemon={attackPokemon}
                    handleSelectTerasuTypeForAttackPokemon={handleSelectTerasuTypeForAttackPokemon}
                    handleSelectTerasuStateForAttackPokemon={handleSelectTerasuStateForAttackPokemon}
                  />

                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, ml: 0.5 }}>
                    <AttackPokemonItemInput
                      attackPokemon={attackPokemon}
                      handleSelectItemForAttackPokemon={handleSelectItemForAttackPokemon}
                      getOffenseItems={getOffenseItems}
                    />

                    <AttackPokemonAbilityInput
                      attackPokemon={attackPokemon}
                      handleSelectAbilityForAttackPokemon={handleSelectAbilityForAttackPokemon}
                      handleUpdateKodaiQuarkStat={handleUpdateKodaiQuarkStat}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <OffencePokemonEffortInput
                      attackPokemon={attackPokemon}
                      handleUpdateAttackPokemonStats={handleUpdateAttackPokemonStats}
                    />

                    <OffencePokemonRankInput
                      attackPokemon={attackPokemon}
                      getRankTypeForMove={getRankTypeForMove}
                      handleUpdateRankCorrection={handleUpdateRankCorrection}
                    />
                  </Box>

                  <OffencePokemonOtherSettingInput
                    attackPokemon={attackPokemon}
                    handleUpdateAttackPokemonStatus={handleUpdateAttackPokemonStatus}
                    handleUpdateCriticalHit={handleUpdateCriticalHit}
                    handleUpdateCustomPower={handleUpdateCustomPower}
                    handleUpdateJuuden={handleUpdateJuuden}
                    handleUpdateDoublePower={handleUpdateDoublePower}
                  />
                </>
              )}
            </AttackPokemonCardFrame>
          );
        })}
      </Box>
    ) : (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        flexDirection: 'column',
        minHeight: '200px'
      }}>
        <Typography variant="h6" color="text.secondary" textAlign="center">
          攻撃ポケモンを追加してください
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
          上記から攻撃ポケモンを選択して追加できます
        </Typography>
      </Box>
    )}
  </>
}
export default DefenceMode
